import { CartLinesUpdateEvent } from '@shopify/events';

const GIFT_EVENT = 'pokegeek:cart-contract-updated';
const INITIAL_DATA_ID = 'pokegeek-cart-contract-data';
const GIFT_PROPERTY = '_pg_cart_gift';
const GIFT_TIER_PROPERTY = '_pg_gift_tier';
const TIER_IDS = Object.freeze(['bonus', 'premium', 'collector']);

const TRANSLATIONS = Object.freeze({
  fr: Object.freeze({
    unlock_bonus: 'Encore {amount} pour débloquer votre Cadeau Bonus',
    bonus: 'Cadeau Bonus débloqué',
    premium: 'Cadeau Premium débloqué',
    collector: 'Cadeau Collector débloqué',
    next_premium: 'Encore {amount} avant le Cadeau Premium.',
    next_collector: 'Encore {amount} avant le Cadeau Collector.',
    replacement: 'Le cadeau du palier supérieur remplace le précédent.',
    thresholds: '49 € — Pack Bonus · 79 € — Pack Premium · 109 € — Pack Collector',
    composition: 'Composition du cadeau variable selon les arrivages, sans valeur contractuelle.',
    progress_label: 'Progression vers le Cadeau Collector',
    preview: 'Aperçu — ajout automatique désactivé.',
  }),
  en: Object.freeze({
    unlock_bonus: '{amount} more to unlock your Bonus Gift',
    bonus: 'Bonus Gift unlocked',
    premium: 'Premium Gift unlocked',
    collector: 'Collector Gift unlocked',
    next_premium: '{amount} more until the Premium Gift.',
    next_collector: '{amount} more until the Collector Gift.',
    replacement: 'The higher-tier gift replaces the previous one.',
    thresholds: '€49 — Bonus Pack · €79 — Premium Pack · €109 — Collector Pack',
    composition: 'Gift contents may vary depending on arrivals and have no contractual value.',
    progress_label: 'Progress towards the Collector Gift',
    preview: 'Preview — automatic addition is disabled.',
  }),
  es: Object.freeze({
    unlock_bonus: 'Faltan {amount} para desbloquear tu Regalo Bonus',
    bonus: 'Regalo Bonus desbloqueado',
    premium: 'Regalo Premium desbloqueado',
    collector: 'Regalo Collector desbloqueado',
    next_premium: 'Faltan {amount} para el Regalo Premium.',
    next_collector: 'Faltan {amount} para el Regalo Collector.',
    replacement: 'El regalo del nivel superior sustituye al anterior.',
    thresholds: '49 € — Pack Bonus · 79 € — Pack Premium · 109 € — Pack Collector',
    composition: 'El contenido del regalo puede variar según las llegadas y no tiene valor contractual.',
    progress_label: 'Progreso hacia el Regalo Collector',
    preview: 'Vista previa — la adición automática está desactivada.',
  }),
  de: Object.freeze({
    unlock_bonus: 'Noch {amount} bis zum Bonus-Geschenk',
    bonus: 'Bonus-Geschenk freigeschaltet',
    premium: 'Premium-Geschenk freigeschaltet',
    collector: 'Collector-Geschenk freigeschaltet',
    next_premium: 'Noch {amount} bis zum Premium-Geschenk.',
    next_collector: 'Noch {amount} bis zum Collector-Geschenk.',
    replacement: 'Das Geschenk der höheren Stufe ersetzt das vorherige.',
    thresholds: '49 € — Bonus-Paket · 79 € — Premium-Paket · 109 € — Collector-Paket',
    composition: 'Der Geschenk-Inhalt kann je nach Wareneingang variieren und ist nicht verbindlich.',
    progress_label: 'Fortschritt zum Collector-Geschenk',
    preview: 'Vorschau — automatisches Hinzufügen ist deaktiviert.',
  }),
  it: Object.freeze({
    unlock_bonus: 'Ancora {amount} per sbloccare il Regalo Bonus',
    bonus: 'Regalo Bonus sbloccato',
    premium: 'Regalo Premium sbloccato',
    collector: 'Regalo Collector sbloccato',
    next_premium: 'Ancora {amount} al Regalo Premium.',
    next_collector: 'Ancora {amount} al Regalo Collector.',
    replacement: 'Il regalo della soglia superiore sostituisce il precedente.',
    thresholds: '49 € — Pack Bonus · 79 € — Pack Premium · 109 € — Pack Collector',
    composition: 'Il contenuto del regalo può variare in base agli arrivi e non ha valore contrattuale.',
    progress_label: 'Avanzamento verso il Regalo Collector',
    preview: 'Anteprima — aggiunta automatica disattivata.',
  }),
  nl: Object.freeze({
    unlock_bonus: 'Nog {amount} om uw Bonuscadeau vrij te spelen',
    bonus: 'Bonuscadeau vrijgespeeld',
    premium: 'Premiumcadeau vrijgespeeld',
    collector: 'Collectorcadeau vrijgespeeld',
    next_premium: 'Nog {amount} tot het Premiumcadeau.',
    next_collector: 'Nog {amount} tot het Collectorcadeau.',
    replacement: 'Het cadeau van de hogere drempel vervangt het vorige.',
    thresholds: '€ 49 — Bonuspack · € 79 — Premiumpack · € 109 — Collectorpack',
    composition: 'De inhoud van het cadeau kan per levering verschillen en is niet bindend.',
    progress_label: 'Voortgang naar het Collectorcadeau',
    preview: 'Voorbeeld — automatisch toevoegen is uitgeschakeld.',
  }),
});

function isObject(value) {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function normalizeId(value) {
  if (value === null || value === undefined || value === '') return null;
  return String(value).replace(/^gid:\/\/shopify\/(?:Product|ProductVariant)\//, '');
}

function normalizeIds(values) {
  return Array.isArray(values) ? values.map(normalizeId).filter(Boolean) : [];
}

function normalizeGiftRuntimeConfig(input) {
  const source = isObject(input) ? input : {};
  const ui = isObject(source.gift_ui) ? source.gift_ui : source;
  const trusted = isObject(source.trusted_config) ? source.trusted_config : source;
  const byTierSource = isObject(trusted.gift_variant_ids_by_tier) ? trusted.gift_variant_ids_by_tier : {};
  const giftVariantIdsByTier = Object.freeze(Object.fromEntries(
    TIER_IDS.map((tier) => [tier, normalizeId(byTierSource[tier])])
  ));

  return Object.freeze({
    enabled: ui.enabled === true,
    preview_only: ui.preview_only === true,
    gift_product_ids: Object.freeze(normalizeIds(trusted.gift_product_ids)),
    gift_variant_ids: Object.freeze([
      ...new Set([...normalizeIds(trusted.gift_variant_ids), ...Object.values(giftVariantIdsByTier).filter(Boolean)]),
    ]),
    gift_variant_ids_by_tier: giftVariantIdsByTier,
  });
}

function isTrustedGiftLine(line, config) {
  const runtime = normalizeGiftRuntimeConfig(config);
  const productId = normalizeId(line?.product_id);
  const variantId = normalizeId(line?.variant_id);
  return runtime.gift_product_ids.includes(productId) || runtime.gift_variant_ids.includes(variantId);
}

function inspectGiftLines(contract, config) {
  const lines = Array.isArray(contract?.lines)
    ? contract.lines.filter((line) => isTrustedGiftLine(line, config))
    : [];
  return Object.freeze({
    count: lines.length,
    keys: Object.freeze(lines.map((line) => String(line.key))),
    lines: Object.freeze(lines.map((line) => Object.freeze({
      key: String(line.key),
      product_id: normalizeId(line.product_id),
      variant_id: normalizeId(line.variant_id),
      quantity: Number.isSafeInteger(line.quantity) ? line.quantity : 0,
    }))),
  });
}

function giftMutationPlan(action, expectedVariantId, expectedTier, keys, quantity, reason) {
  return Object.freeze({
    action,
    expected_variant_id: expectedVariantId,
    expected_tier: expectedTier,
    existing_line_keys: Object.freeze([...keys]),
    quantity,
    reason,
  });
}

function planGiftMutation({ contract, config } = {}) {
  const runtime = normalizeGiftRuntimeConfig(config);
  const state = isObject(contract?.gift_state) ? contract.gift_state : {};
  const inspection = inspectGiftLines(contract, runtime);
  const unsupported = state.supported !== true || contract?.currency !== 'EUR';
  const empty = contract?.is_empty === true || !Number.isSafeInteger(contract?.gift_eligible_subtotal)
    || contract.gift_eligible_subtotal <= 0;
  const expectedTier = TIER_IDS.includes(state.tier) ? state.tier : null;
  const expectedVariantId = expectedTier ? runtime.gift_variant_ids_by_tier[expectedTier] : null;

  if (unsupported || empty || expectedTier === null || expectedVariantId === null) {
    return inspection.count > 0
      ? giftMutationPlan('remove', null, null, inspection.keys, 0, unsupported ? 'unsupported_currency' : 'no_eligible_tier')
      : giftMutationPlan('none', null, null, inspection.keys, 0, unsupported ? 'unsupported_currency' : 'no_eligible_tier');
  }
  if (inspection.count === 0) {
    return giftMutationPlan('add', expectedVariantId, expectedTier, [], 1, 'missing_expected_gift');
  }
  if (inspection.count > 1) {
    return giftMutationPlan('consolidate', expectedVariantId, expectedTier, inspection.keys, 1, 'multiple_gift_lines');
  }

  const line = inspection.lines[0];
  if (line.variant_id !== expectedVariantId) {
    return giftMutationPlan('replace', expectedVariantId, expectedTier, inspection.keys, 1, 'tier_mismatch');
  }
  if (line.quantity !== 1) {
    return giftMutationPlan('replace', expectedVariantId, expectedTier, inspection.keys, 1, 'quantity_mismatch');
  }
  return giftMutationPlan('none', expectedVariantId, expectedTier, inspection.keys, 1, 'already_correct');
}

function ensureJsRoute(value, fallback) {
  const route = typeof value === 'string' && value !== '' ? value : fallback;
  return route.endsWith('.js') ? route : `${route}.js`;
}

function sectionPayload(sections, sectionsUrl) {
  const names = Array.isArray(sections) ? sections.filter((value) => typeof value === 'string' && value !== '') : [];
  return names.length > 0 ? { sections: names, sections_url: sectionsUrl || '/' } : {};
}

async function ajaxJson(fetchImpl, url, body) {
  const response = await fetchImpl(url, {
    method: 'POST',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify(body),
  });
  if (!response?.ok) throw new Error(`Gift cart mutation failed (${response?.status ?? 'network'})`);
  return response.json();
}

let activeMutation = null;
let activeMutationSignature = null;

async function executeGiftPlan(plan, {
  config,
  fetchImpl = globalThis.fetch,
  routes = {},
  sections = ['cart-drawer-section', 'main-cart'],
  sectionsUrl = globalThis.location?.pathname || '/',
  refreshContract = globalThis.window?.PokeGeekCartContract?.refreshCurrentCartContract,
} = {}) {
  const runtime = normalizeGiftRuntimeConfig(config);
  if (!runtime.enabled) return Object.freeze({ executed: false, reason: 'disabled' });
  if (!isObject(plan) || plan.action === 'none') {
    return Object.freeze({ executed: false, reason: plan?.reason || 'no_action' });
  }
  if (typeof fetchImpl !== 'function') throw new TypeError('A fetch implementation is required');

  const signature = JSON.stringify(plan);
  if (activeMutation) {
    return Object.freeze({
      executed: false,
      reason: activeMutationSignature === signature ? 'duplicate_in_flight' : 'mutation_in_progress',
    });
  }

  const root = String(globalThis.window?.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
  const addUrl = ensureJsRoute(routes.add || globalThis.window?.Theme?.routes?.cart_add_url, `${root}cart/add`);
  const changeUrl = ensureJsRoute(routes.change || globalThis.window?.Theme?.routes?.cart_change_url, `${root}cart/change`);
  const updateUrl = ensureJsRoute(routes.update || globalThis.window?.Theme?.routes?.cart_update_url, `${root}cart/update`);
  const sectionsPayload = sectionPayload(sections, sectionsUrl);

  activeMutationSignature = signature;
  activeMutation = (async () => {
    const uniqueKeys = [...new Set((plan.existing_line_keys || []).map(String))];
    let latest = null;
    const removeAll = async () => {
      const updates = Object.fromEntries(uniqueKeys.map((key) => [key, 0]));
      if (Object.keys(updates).length > 0) latest = await ajaxJson(fetchImpl, updateUrl, { updates, ...sectionsPayload });
    };
    const addExpected = async () => {
      if (!plan.expected_variant_id || !TIER_IDS.includes(plan.expected_tier)) {
        throw new Error('Expected gift variant or tier is missing');
      }
      latest = await ajaxJson(fetchImpl, addUrl, {
        items: [{
          id: plan.expected_variant_id,
          quantity: 1,
          properties: { [GIFT_PROPERTY]: 'true', [GIFT_TIER_PROPERTY]: plan.expected_tier },
        }],
        ...sectionsPayload,
      });
    };

    if (plan.action === 'add') {
      await addExpected();
    } else if (plan.action === 'remove') {
      await removeAll();
    } else if (plan.action === 'replace' && plan.reason === 'quantity_mismatch' && uniqueKeys.length === 1) {
      latest = await ajaxJson(fetchImpl, changeUrl, { id: uniqueKeys[0], quantity: 1, ...sectionsPayload });
    } else if (plan.action === 'replace' || plan.action === 'consolidate') {
      await removeAll();
      await addExpected();
    } else {
      throw new Error(`Unsupported gift mutation action: ${plan.action}`);
    }

    const refreshed = typeof refreshContract === 'function' ? await refreshContract('cart-gift-mutation') : null;
    return Object.freeze({
      executed: true,
      action: plan.action,
      reason: plan.reason,
      sections: Object.freeze(isObject(latest?.sections) ? { ...latest.sections } : {}),
      item_count: Number.isSafeInteger(refreshed?.item_count) ? refreshed.item_count : null,
    });
  })();

  try {
    return await activeMutation;
  } finally {
    activeMutation = null;
    activeMutationSignature = null;
  }
}

function readInitialData(documentRef = globalThis.document) {
  const node = documentRef?.getElementById?.(INITIAL_DATA_ID);
  if (!node?.textContent) return {};
  try {
    const parsed = JSON.parse(node.textContent);
    return isObject(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

function normalizedLocale(value) {
  const locale = String(value || 'fr').toLowerCase().split('-')[0];
  return Object.hasOwn(TRANSLATIONS, locale) ? locale : 'fr';
}

function formatMoney(value, locale, currency = 'EUR') {
  if (!Number.isSafeInteger(value) || value < 0) return '';
  return new Intl.NumberFormat(locale, {
    style: 'currency', currency, minimumFractionDigits: 2, maximumFractionDigits: 2,
  }).format(value / 100);
}

function interpolate(template, amount) {
  return String(template || '').replace('{amount}', amount);
}

function previewContextAuthorized(element, documentRef = globalThis.document) {
  if (element?.dataset?.previewAuthorized === 'true') return true;
  if (globalThis.window?.Shopify?.designMode === true) return true;
  return Boolean(documentRef?.getElementById?.('PBarNextFrame'));
}

const HTMLElementBase = typeof HTMLElement === 'undefined' ? class {} : HTMLElement;

class PokeGeekCartGifts extends HTMLElementBase {
  #config = normalizeGiftRuntimeConfig({});
  #contract = null;
  #locale = 'fr';
  #connected = false;

  connectedCallback() {
    if (this.#connected) return;
    this.#connected = true;
    this.#config = normalizeGiftRuntimeConfig(readInitialData());
    this.#locale = normalizedLocale(this.dataset.locale);
    document.addEventListener(GIFT_EVENT, this.#handleContractUpdate);
    this.#contract = globalThis.window?.PokeGeekCartContract?.getCurrentCartContract?.() || null;
    this.#render();
    this.#synchronize();
  }

  disconnectedCallback() {
    if (!this.#connected) return;
    this.#connected = false;
    document.removeEventListener(GIFT_EVENT, this.#handleContractUpdate);
  }

  #handleContractUpdate = (event) => {
    this.#contract = event?.detail?.contract || null;
    this.#render();
    this.#synchronize();
  };

  async #synchronize() {
    const plan = planGiftMutation({ contract: this.#contract, config: this.#config });
    if (!this.#config.enabled || plan.action === 'none') return;
    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(new CartLinesUpdateEvent({
      action: plan.action === 'add' ? 'add' : plan.action === 'remove' ? 'remove' : 'update',
      context: 'cart-gift', lines: [], promise: deferred.promise,
    }));
    try {
      const result = await executeGiftPlan(plan, { config: this.#config });
      deferred.resolve({
        cart: null,
        detail: { itemCount: result.item_count, sections: result.sections, source: 'pg-cart-gifts', didError: false },
      });
      if (result.executed !== true) return;
      const rerender = () => {
        if (!this.isConnected) return;
        this.#render();
        this.#synchronize();
      };
      if (typeof globalThis.requestAnimationFrame === 'function') {
        globalThis.requestAnimationFrame(() => globalThis.requestAnimationFrame(rerender));
      } else {
        globalThis.setTimeout?.(rerender, 0);
      }
    } catch (error) {
      deferred.reject(error);
      console.warn('[pg-cart-gifts] Mutation skipped:', error);
    }
  }

  #render() {
    const canRender = this.#config.enabled || (this.#config.preview_only && previewContextAuthorized(this));
    const state = isObject(this.#contract?.gift_state) ? this.#contract.gift_state : null;
    if (!canRender || state?.supported !== true || this.#contract?.currency !== 'EUR') {
      this.hidden = true;
      return;
    }

    const text = TRANSLATIONS[this.#locale];
    const amount = formatMoney(state.remaining, this.#locale, 'EUR');
    const primary = this.querySelector('[data-gift-primary]');
    const secondary = this.querySelector('[data-gift-secondary]');
    const thresholds = this.querySelector('[data-gift-thresholds]');
    const composition = this.querySelector('[data-gift-composition]');
    const progress = this.querySelector('[data-gift-progress]');
    const progressValue = this.querySelector('[data-gift-progress-value]');
    const preview = this.querySelector('[data-gift-preview]');
    let primaryText = '';
    let secondaryText = '';

    if (state.tier === null) primaryText = interpolate(text.unlock_bonus, amount);
    else if (state.tier === 'bonus') {
      primaryText = text.bonus;
      secondaryText = `${interpolate(text.next_premium, amount)} ${text.replacement}`;
    } else if (state.tier === 'premium') {
      primaryText = text.premium;
      secondaryText = `${interpolate(text.next_collector, amount)} ${text.replacement}`;
    } else if (state.tier === 'collector') {
      primaryText = text.collector;
      secondaryText = text.replacement;
    }

    if (primary) primary.textContent = primaryText;
    if (secondary) {
      secondary.textContent = secondaryText;
      secondary.hidden = secondaryText === '';
    }
    if (thresholds) thresholds.textContent = text.thresholds;
    if (composition) composition.textContent = text.composition;
    const eligibleSubtotal = Number.isSafeInteger(this.#contract?.gift_eligible_subtotal)
      ? Math.max(0, this.#contract.gift_eligible_subtotal)
      : 0;
    const progressRatio = Math.min(1, eligibleSubtotal / 10900);
    if (progress) {
      progress.setAttribute('aria-valuenow', String(Math.min(10900, eligibleSubtotal)));
      progress.setAttribute('aria-label', text.progress_label);
    }
    if (progressValue) progressValue.style.setProperty('--pg-gift-progress', `${progressRatio * 100}%`);
    if (preview) {
      preview.textContent = this.#config.enabled ? '' : text.preview;
      preview.hidden = this.#config.enabled;
    }
    this.dataset.tier = state.tier || 'none';
    this.hidden = false;
  }
}

if (typeof window !== 'undefined' && typeof document !== 'undefined' && typeof customElements !== 'undefined') {
  if (!customElements.get('pg-cart-gifts')) customElements.define('pg-cart-gifts', PokeGeekCartGifts);
}

export {
  GIFT_EVENT,
  TRANSLATIONS,
  executeGiftPlan,
  formatMoney,
  inspectGiftLines,
  normalizeGiftRuntimeConfig,
  planGiftMutation,
  previewContextAuthorized,
};
